import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { UserService } from 'app/core/services/user.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { concatMap, filter, map } from 'rxjs/operators';
import { Observable, forkJoin, concat, Subscription, of } from 'rxjs';
import { ConfigurationService } from 'app/core/services/configuration.service';
import { User } from 'app/core/models/user';
import { Roles } from 'app/core/models/roles';
import { SupplierService } from 'app/core/services/supplier.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { Supplier } from 'app/core/models/supplier';
import { Items } from 'app/core/models/items';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';
import { Merchant } from 'app/core/models/merchant';
import { KeycloakService } from 'security/keycloak.service';
import { BaseComponent } from 'app/base/base.component';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.css']
})
export class CreateUserComponent extends BaseComponent implements OnInit {

  userForm: FormGroup;
  supplierSub: Subscription;
  merchantSub: Subscription;

  infoUser: any;
  selectableRoles: Array<Roles> = [];
  assignedRoles: Array<Roles> = [];
  suppliers: Array<Supplier> = [];
  merchants: Array<Merchant> = [];
  allSuppliers: Array<Supplier>;
  allMerchants: Array<Merchant>;
  allSuppliersInit: Array<Supplier>;
  allMerchantsInit: Array<Merchant>;
  supplierControl: FormControl = new FormControl();
  merchantControl: FormControl = new FormControl();

  isSupplierAdding: boolean = false;
  isMerchantAdding: boolean = false;

  isMerchantRead: boolean = false;
  isSupplierRead: boolean = false;

  isLinked: boolean = false;

  canLinkToMerchant: boolean = false;
  canLinkToSupplier: boolean = false;

  needLink: boolean = true;

  localeItems = [
    { value: "fr", label: "LANGUAGE_FRENCH_FRANCE" },
    { value: "en", label: "LANGUAGE_ENGLISH_UNITED_STATES" }
  ]

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private notifService: NotifMessageService,
    public router: Router,
    private location: Location,
    private configService: ConfigurationService,
    private supplierService: SupplierService,
    private merchantService: MerchantService,
    private roleGuard: AuthenticatedRouteGuard,
    private keycloakService: KeycloakService,
    private translateService: TranslateService) { super(router) }

  ngOnInit() {
    this.initForm();

    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        this.getAllRoles();
        concat(
          this.getMerchants(),
          this.getSuppliers(),
        ).subscribe({
          next: () => console.log("get entities done"),
          complete: () => this.LinkEntityToDefaultValue()
        });
      }
    });
  }

  ngOnDestroy() {
    if (this.merchantSub) {
      this.merchantSub.unsubscribe();
    }
    if (this.supplierSub) {
      this.supplierSub.unsubscribe();
    }
  }

  get user() {
    return this.userForm.controls;
  }

  initForm() {
    this.userForm = this.formBuilder.group({
      username: new FormControl('', Validators.required),
      firstName: new FormControl('', Validators.compose([
        Validators.required,
        Validators.pattern(this.contactNameRegex)
      ])),
      lastName: new FormControl('', Validators.compose([
        Validators.required,
        Validators.pattern(this.contactNameRegex)
      ])),
      email: new FormControl('', Validators.compose([
        Validators.required,
        Validators.pattern(this.emailRegex)
      ])),
      locale: new FormControl('en', Validators.required)
    });
  }

  onSubmitForm() {
    let attributes: {
      locale: Array<string>;
      merchantId?: Array<number>;
      supplierId?: Array<number>;
    } = {
      locale: this.userForm.get("locale").value
    };
    if (this.suppliers.length > 0) {
      attributes = { ...attributes, supplierId: [this.suppliers[0].id] };
    }
    if (this.merchants.length > 0) {
      attributes = { ...attributes, merchantId: [this.merchants[0].id] };
    }
    let newUser: User = {
      username: this.userForm.get("username").value,
      firstName: this.userForm.get("firstName").value,
      lastName: this.userForm.get("lastName").value,
      email: this.userForm.get("email").value,
      enabled: true,
      attributes: attributes
    };

    forkJoin([this.userService.isEmailExists(newUser.email),
    this.userService.isUsernameExists(newUser.username)]).subscribe(
      ([resEmail, resUserName]) => {
        if (resEmail) {
          this.notifService.notifyWarn('USER_EMAIL_EXIST');
        }
        if (resUserName) {
          this.notifService.notifyWarn('USER_USERNAME_EXIST');
        }
        if (!resEmail && !resUserName) {
          this.createUser(newUser);
        }
      }
    )
  }

  createUser(newUser: User) {
    this.userService.createUser(newUser).subscribe(user => {
      let passwordCreated = false;
      let rolesCreated = false;
      this.rolesAndPassword(newUser, this.assignedRoles).subscribe(res => {
        if (res === "password") {
          passwordCreated = true;
          this.notifService.notifySuccess('EMAIL_SENT');
        } else if (res === "roles") {
          rolesCreated = true;
          this.notifService.notifySuccess('ROLES_ADDED');
        }
        if (passwordCreated && rolesCreated) {
          this.router.navigate(['/user']);
          this.notifService.notifySuccess('USER_CREATED');
        }
      }
      );
    }
    );

  }

  rolesAndPassword(user, assignedRoles): Observable<any> {
    let roles: Array<Roles> = [];
    assignedRoles.forEach(role => {
      delete role.display
      roles.push(role);
    });
    return this.userService.getUserByUsername(user.username).pipe(
      concatMap(users => {
        let myUser = users[0];
        return concat(
          this.userService.addRoles(myUser, roles).pipe(map(() => "roles")),
          this.userService.updatePassword(myUser).pipe(map(() => "password"))
        );
      }
      )
    )
  }

  getAllRoles() {
    this.userService.getAllRoles().pipe(
      map(roles => {
        return roles.filter(role => {
          let roleEnabled: boolean = false;
          if (this.roleGuard.isEnabled("ADMIN_WORLDLINE")) {
            roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && role.name !== "ADMIN_WORLDLINE";
          } else {
            if (this.roleGuard.isEnabled("ADMIN_MERCHANT_PETROL")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("PETROL") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_MERCHANT_OPH")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("OPH") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_SUPPLIER")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("SUPPLIER") || role.name.startsWith("VIEWER"));
            }
            if (this.roleGuard.isEnabled("ADMIN_SUPPLIER") && this.roleGuard.isEnabled("ADMIN_MERCHANT_PETROL")) {
              roleEnabled = this.configService.config.security.keycloak.roles.includes(role.name) && (role.name.endsWith("PETROL") || role.name.endsWith("SUPPLIER") || role.name.startsWith("VIEWER"));
            }
          }
          return roleEnabled;
        });
      })
    ).subscribe(roles => {
      if (roles) {
        roles.forEach(role => {
          this.selectableRoles.push(role = { ...role, display: this.translateService.instant(role.name) });
        })
      }
    });
  }

  getSuppliers() {
    if (this.roleGuard.isEnabled('SUPPLIER_READ')) {
      return this.supplierService.getAllSuppliers().pipe(map(suppliers => {
        this.allSuppliers = suppliers.content;
        this.allSuppliersInit = suppliers.content;
        if (this.allSuppliers && this.allSuppliers.length > 0) {
          this.supplierControl = new FormControl(this.allSuppliers[0].id);
          this.isSupplierRead = true;
        }
      }));
    } else {
      return of(null);
    }
  }

  getMerchants() {
    if (this.roleGuard.isEnabled('MERCHANT_READ')) {
      return this.merchantService.getAllMerchants().pipe(map(merchants => {
        this.allMerchants = merchants.content;
        this.allMerchantsInit = merchants.content;
        if (this.allMerchants && this.allMerchants.length > 0) {
          this.merchantControl = new FormControl(this.allMerchants[0].id);
          this.isMerchantRead = true;
        }
      }));
    } else {
      return of(null);
    }
  }

  addMerchant() {
    this.allMerchants = Object.assign([], this.allMerchantsInit);
    this.allMerchants.forEach(merchant => {
      if (this.merchants.includes(merchant)) {
        let index = this.allMerchants.findIndex(mer => mer == merchant);
        this.allMerchants.splice(index, 1);
      }
    });
    this.merchantControl = new FormControl(this.allMerchants[0].id);
    this.isMerchantAdding = true;
  }

  addSupplier() {
    this.allSuppliers = Object.assign([], this.allSuppliersInit);
    this.allSuppliers.forEach(supplier => {
      if (this.suppliers.includes(supplier)) {
        let index = this.allSuppliers.findIndex(supp => supp == supplier);
        this.allSuppliers.splice(index, 1);
      }
    });
    this.supplierControl = new FormControl(this.allSuppliers[0].id);
    this.isSupplierAdding = true;
  }

  cancel() {
    this.isSupplierAdding = false;
    this.isMerchantAdding = false;
  }

  linkSupplier(MyId?) {
    if (this.suppliers.length > 0) this.suppliers.splice(0, 1);
    let supplier = this.allSuppliers.find(supp => MyId ? supp.id == MyId : supp.id == this.supplierControl.value);
    this.suppliers.push(supplier);
    this.isSupplierAdding = false;
    this.checkLinked();
  }

  removeSupplier(id) {
    let index = this.suppliers.findIndex(supplier => supplier.id === id);
    this.suppliers.splice(index, 1);
    this.isSupplierAdding = false;
    this.checkLinked();
  }

  linkMerchant(MyId?) {
    if (this.merchants.length > 0) this.merchants.splice(0, 1);
    let merchant = this.allMerchants.find(mer => MyId ? mer.id == MyId : mer.id == this.merchantControl.value);
    this.merchants.push(merchant);
    this.isMerchantAdding = false;
    this.checkLinked();
  }

  removeMerchant(id) {
    let index = this.merchants.findIndex(merchant => merchant.id === id);
    this.merchants.splice(index, 1);
    this.isMerchantAdding = false;
    this.checkLinked();
  }

  checkLinked() {
    if (this.suppliers.length > 0 || this.merchants.length > 0) {
      this.isLinked = true;
    } else {
      this.isLinked = false;
    }
  }

  back() {
    this.location.back();
  }

  cancelForm() {
    this.location.back();
  }

  isLinkedToEntity(): boolean {
    if (!this.needLink) {
      return true;
    } else if (this.isLinked) {
      return true;
    } else {
      return false;
    }
  }

  LinkEntityToDefaultValue() {
    if (this.roleGuard.isEnabled('ADMIN_MERCHANT_PETROL')) {
      this.canLinkToMerchant = true;
    }

    if (this.roleGuard.isEnabled('ADMIN_MERCHANT_OPH')) {
      this.canLinkToMerchant = true;
    }

    if (this.roleGuard.isEnabled('ADMIN_SUPPLIER')) {
      this.canLinkToSupplier = true;
    }

    if (!this.roleGuard.isEnabled('ADMIN_WORLDLINE')) {
      this.infoUser = this.keycloakService.userInfo;
      let sub = this.userService.getUserById(this.infoUser.sub).subscribe(user => {
        if (user.attributes.merchantId) {
          this.linkMerchant(user.attributes.merchantId[0]);
        }
        else if (user.attributes.supplierId) {
          this.linkSupplier(user.attributes.supplierId[0]);
        }
      })
    }
  }

  checkRoles() {
    if (this.assignedRoles.some(role => role.name === "ADMIN_WORLDLINE")) {
      this.needLink = false;
    } else {
      this.needLink = true;
    }
  }

  transSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item.label).toLocaleLowerCase().indexOf(term) > -1;
  }
}
